import { useEffect, useMemo, useState } from 'react';
import {
  FLEET,
  FLEET_BY_KIND,
  type BattleshipAction,
  type BattleshipView,
  type PlayerCard,
} from '@bn/shared';
import { Alert, Badge, Button, Card } from '@/components/ui';
import { Board, type CellRender } from './Board';
import { PlacementBoard } from './PlacementBoard';
import { formatDuration, secondsLeft } from '@/lib/time';
import { buildOwnGrid, buildTargetGrid, cellLabel } from './helpers';

interface Props {
  view: BattleshipView;
  you: PlayerCard;
  opponent: PlayerCard;
  opponentOnline: boolean;
  clockOffset: number;
  onAction: (action: BattleshipAction) => Promise<void>;
}

export function BattleshipMatchView({
  view,
  you,
  opponent,
  opponentOnline,
  clockOffset,
  onAction,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(() =>
    secondsLeft(view.deadlineAt, clockOffset),
  );

  useEffect(() => {
    setRemaining(secondsLeft(view.deadlineAt, clockOffset));
    if (view.deadlineAt === null || view.phase === 'finished') return;
    const timer = setInterval(() => {
      setRemaining(secondsLeft(view.deadlineAt, clockOffset));
    }, 1000);
    return () => clearInterval(timer);
  }, [view.deadlineAt, view.phase, clockOffset]);

  const ownGrid = useMemo(() => buildOwnGrid(view), [view]);
  const targetGrid = useMemo(() => buildTargetGrid(view), [view]);
  const yourTurn = view.turn === you.id && view.phase === 'playing';

  async function send(action: BattleshipAction) {
    setError(null);
    setBusy(true);
    try {
      await onAction(action);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Action refusée.');
    } finally {
      setBusy(false);
    }
  }

  function renderOwn(x: number, y: number): CellRender {
    const cell = ownGrid[y]?.[x];
    if (!cell) return { className: 'bg-slate-800', description: 'Case', disabled: true };

    if (cell.ship && cell.sunk) {
      return {
        className: 'bg-red-600 text-white',
        content: '✖',
        description: `${FLEET_BY_KIND[cell.ship].label} coulé`,
        disabled: true,
      };
    }
    if (cell.ship && cell.hit) {
      return {
        className: 'bg-orange-500 text-slate-950',
        content: '✖',
        description: `${FLEET_BY_KIND[cell.ship].label} touché`,
        disabled: true,
      };
    }
    if (cell.ship) {
      return {
        className: 'bg-slate-300 text-slate-900',
        content: FLEET_BY_KIND[cell.ship].label.charAt(0),
        description: FLEET_BY_KIND[cell.ship].label,
        disabled: true,
      };
    }
    if (cell.miss) {
      return {
        className: 'bg-sky-950 text-sky-400',
        content: '•',
        description: 'Tir adverse manqué',
        disabled: true,
      };
    }
    return {
      className: 'bg-sky-950/70 ring-1 ring-inset ring-sky-900/60',
      description: 'Mer',
      disabled: true,
    };
  }

  function renderTarget(x: number, y: number): CellRender {
    const state = targetGrid[y]?.[x] ?? 'inconnu';
    const label = cellLabel(x, y);

    switch (state) {
      case 'coule':
        return {
          className: 'bg-red-600 text-white',
          content: '✖',
          description: `${label} — navire coulé`,
          disabled: true,
        };
      case 'touche':
        return {
          className: 'bg-orange-500 text-slate-950',
          content: '✖',
          description: `${label} — touché`,
          disabled: true,
        };
      case 'rate':
        return {
          className: 'bg-slate-800 text-slate-500',
          content: '•',
          description: `${label} — à l'eau`,
          disabled: true,
        };
      default:
        return {
          className: yourTurn
            ? 'bg-sky-950/70 ring-1 ring-inset ring-sky-900/60 hover:bg-sky-500/40 cursor-crosshair'
            : 'bg-sky-950/70 ring-1 ring-inset ring-sky-900/60',
          description: `${label} — tirer ici`,
          disabled: !yourTurn || busy,
        };
    }
  }

  if (view.phase === 'placement') {
    return (
      <div className="space-y-4">
        <StatusBar
          text="Placement des flottes"
          remaining={remaining}
          you={you}
          opponent={opponent}
          opponentOnline={opponentOnline}
          view={view}
        />
        {error && <Alert>{error}</Alert>}
        <Card>
          <PlacementBoard
            waiting={view.you.ready}
            onSubmit={(ships) => send({ type: 'place', ships })}
          />
        </Card>
      </div>
    );
  }

  const finished = view.phase === 'finished';
  const victory = finished && view.winner === you.id;
  const draw = finished && view.winner === null;

  return (
    <div className="space-y-4">
      <StatusBar
        text={
          finished
            ? 'Partie terminée'
            : yourTurn
              ? 'À vous de jouer'
              : `Au tour de ${opponent.displayName}`
        }
        remaining={finished ? null : remaining}
        you={you}
        opponent={opponent}
        opponentOnline={opponentOnline}
        view={view}
      />

      {finished && (
        <Alert type={victory ? 'succes' : draw ? 'info' : 'erreur'}>
          {draw
            ? 'Partie nulle.'
            : victory
              ? 'Victoire ! La flotte adverse est coulée.'
              : 'Défaite. Votre flotte a été coulée.'}
        </Alert>
      )}
      {error && <Alert>{error}</Alert>}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card
          title="Grille adverse"
          actions={<Badge tone="info">{view.opponent.shipsRemaining} navires debout</Badge>}
        >
          <Board
            renderCell={renderTarget}
            onCellClick={
              yourTurn ? (x, y) => void send({ type: 'fire', x, y }) : undefined
            }
            dimmed={!yourTurn}
          />
          <p className="mt-3 text-xs text-slate-400">
            {finished
              ? 'La flotte adverse est révélée.'
              : yourTurn
                ? 'Cliquez sur une case pour tirer.'
                : 'Attendez votre tour.'}
          </p>
        </Card>

        <Card
          title="Votre flotte"
          actions={<Badge>{view.you.shipsRemaining} navires debout</Badge>}
        >
          <Board renderCell={renderOwn} />
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {FLEET.map((spec) => {
              const ship = view.you.ships.find((s) => s.kind === spec.kind);
              const sunk = ship?.hits.every(Boolean) ?? false;
              return (
                <li key={spec.kind}>
                  <Badge tone={sunk ? 'alerte' : 'neutre'}>
                    {spec.label}
                    {sunk ? ' — coulé' : ''}
                  </Badge>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      {!finished && (
        <div className="flex justify-end">
          <Button
            variant="danger"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Abandonner la partie ? La victoire ira à votre adversaire.')) {
                void send({ type: 'resign' });
              }
            }}
          >
            Abandonner
          </Button>
        </div>
      )}
    </div>
  );
}

function StatusBar({
  text,
  remaining,
  you,
  opponent,
  opponentOnline,
  view,
}: {
  text: string;
  remaining: number | null;
  you: PlayerCard;
  opponent: PlayerCard;
  opponentOnline: boolean;
  view: BattleshipView;
}) {
  const urgent = remaining !== null && remaining <= 10;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-700/60 bg-slate-900/60 px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="font-semibold text-slate-100">{text}</span>
        {remaining !== null && (
          <span
            className={`rounded-md px-2 py-0.5 font-mono text-sm ${
              urgent ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-200'
            }`}
          >
            {formatDuration(remaining)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 text-sm text-slate-300">
        <span>
          {you.displayName} <span className="text-slate-500">({you.rating})</span>
        </span>
        <span className="text-slate-500">contre</span>
        <span className="flex items-center gap-1.5">
          {opponent.displayName} <span className="text-slate-500">({opponent.rating})</span>
          <span
            title={opponentOnline ? 'Connecté' : 'Déconnecté'}
            className={`size-2 rounded-full ${opponentOnline ? 'bg-emerald-400' : 'bg-red-500'}`}
          />
        </span>
        <Badge>{view.moves} coups</Badge>
      </div>
    </div>
  );
}
